import { useState, useEffect } from 'react';
import 'chonky/style/main.css';
import { ChonkyActions, FileAction, FileActionData, FileBrowser, FileList, FileSearch, FileToolbar } from 'chonky';
import { fetchCommunities } from '@/services/Communities';
import { fetchLibraryItems, fetchRooms } from '@/services/Rooms';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import intl from 'react-intl-universal';

const FilePicker = ({
  isOpen,
  onCancel,
  callback,
  ixUrl,
  restrictToCommunity,
  defaultRoom,
  styles,
}: {
  isOpen: boolean;
  onCancel: () => void;
  callback: (data: any) => void;
  ixUrl: string;
  restrictToCommunity: string;
  defaultRoom: string;
  styles: any;
}) => {
  const [allItems, setAllItems] = useState({
    root: {
      id: 'root',
      name: 'Root',
      isDir: true,
      childrenIds: [],
    },
  });
  const [currentCommunityId, setCurrentCommunityId] = useState('');
  const [currentRoomId, setCurrentRoomId] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [files, setFiles] = useState([]);
  const [folderChain, setFolderChain] = useState([]);

  const onSelectedFiles = () => {
    callback(
      selectedFiles.map((file) => {
        return {
          destination: file.id,
          destination_version: file.version,
          force: true,
          name: file.name,
          path: file.path,
          type: 'internal-link',
        };
      }),
    );
  };

  const buildItems = ({
    data,
    items,
    parent,
    type,
    isDir,
  }: {
    data: any[];
    items: any[];
    parent: any;
    type?: string;
    isDir?: boolean;
  }) => {
    data.forEach((item) => {
      if (type) {
        item.type = type;
      }
      if (isDir === undefined) {
        item.isDir = item.type !== 'file' && item.type !== 'internal-link' && item.type !== 'external-link';
        item.openable = item.type !== 'file' && item.type !== 'internal-link' && item.type !== 'external-link';
      } else {
        item.isDir = isDir;
      }
      item.size = item.file_size;
      item.selectable = item.type === 'file';
      item.parentId = parent.id;
      item.ext = item.name.split('.').pop();
      if (!parent.childrenIds) {
        parent.childrenIds = [item.id];
      } else {
        parent.childrenIds = [...parent.childrenIds, item.id];
      }
      items[item.id] = item;
    });
    items[parent.id] = parent;
    return items;
  };

  useEffect(() => {
    if (isOpen) {
      initRoot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const initRoot = () => {
    if (allItems['root'].childrenIds.length === 0) {
      fetchCommunities().then((communities) => {
        const items = buildItems({
          data: communities.filter((community) => (restrictToCommunity ? community.id === restrictToCommunity : true)),
          items: { ...allItems },
          parent: { ...allItems.root },
          type: 'community',
          isDir: true,
        });

        // Restrict to defined community if defined
        if (restrictToCommunity) {
          fetchRooms({ communityId: restrictToCommunity }).then((rooms) => {
            const roomItems = buildItems({
              data: rooms,
              items: { ...items },
              parent: { ...items[restrictToCommunity] },
              type: 'room',
              isDir: true,
            });

            if (!defaultRoom) {
              console.log('Setting room content to items...');

              setAllItems(roomItems);
              setCurrentCommunityId(restrictToCommunity);
              setCurrentFolderId(restrictToCommunity);
            } else {
              fetchLibraryItems({
                communityId: restrictToCommunity,
                roomId: defaultRoom,
                nodeId: roomItems[defaultRoom].content?.root_id,
              }).then((libraryItems) => {
                const contentItems = buildItems({
                  data: libraryItems,
                  items: { ...roomItems },
                  parent: { ...roomItems[defaultRoom] },
                });

                console.log('Setting folder content to items...');

                setAllItems(contentItems);
                setCurrentCommunityId(restrictToCommunity);
                setCurrentRoomId(defaultRoom);
                setCurrentFolderId(defaultRoom);
              });
            }
          });
        } else {
          setAllItems(items);
        }
      });
    }
  };

  const handleClickItem = (action: FileAction, data: FileActionData) => {
    if (action.id === ChonkyActions.ChangeSelection.id) {
      setSelectedFiles(data.files.filter((file) => file.type === 'file') || []);
    }

    if (action.id === ChonkyActions.OpenFiles.id) {
      const item = data.target;

      if (item.type === 'file') {
        return;
      } else if (item.type === 'community') {
        setCurrentCommunityId(item.id);
        if (!item.childrenIds) {
          fetchRooms({ communityId: item.id }).then((rooms) => {
            const items = buildItems({
              data: rooms,
              items: { ...allItems },
              parent: { ...allItems[item.id] },
              type: 'room',
              isDir: true,
            });
            setAllItems(items);
          });
        }
      } else if (item.type === 'room') {
        setCurrentRoomId(item.id);
        if (!item.childrenIds) {
          fetchLibraryItems({
            communityId: currentCommunityId,
            roomId: item.id,
            nodeId: item.content?.root_id || 'bbb608c1-c603-422a-92f0-70ca96f11ee5',
          }).then((libraryItems) => {
            const items = buildItems({
              data: libraryItems,
              items: { ...allItems },
              parent: { ...allItems[item.id] },
            });
            setAllItems(items);
          });
        }
      } else if (item.id !== 'root') {
        if (!item.childrenIds) {
          fetchLibraryItems({
            communityId: currentCommunityId,
            roomId: currentRoomId,
            nodeId: item.id,
          }).then((libraryItems) => {
            const items = buildItems({
              data: libraryItems,
              items: { ...allItems },
              parent: { ...allItems[item.id] },
            });
            setAllItems(items);
            setCurrentFolderId(item.id);
          });
        }
      }
      setCurrentFolderId(item.id);
    }
  };

  useEffect(() => {
    console.log('Refreshing structure...');

    const chain = [];
    const folder = allItems[currentFolderId];
    if (folder) {
      console.log('Refreshing folder content...');
      let currentFolder = folder;
      while (currentFolder) {
        chain.unshift(currentFolder);
        const parentId = currentFolder.parentId;
        currentFolder = parentId ? allItems[parentId] : null;
      }
      setFiles(folder.childrenIds ? folder.childrenIds.map((id) => allItems[id]) : []);
    }
    setFolderChain(chain);
  }, [allItems, currentFolderId]);

  return (
    <Dialog
      onClose={onCancel}
      open={isOpen}
      style={styles}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '900px',
            maxWidth: '900px',
            height: '800px',
          },
        },
      }}
    >
      <DialogContent>
        <FileBrowser
          files={files}
          defaultFileViewActionId={ChonkyActions.EnableListView.id}
          folderChain={folderChain}
          onFileAction={handleClickItem}
        >
          <FileToolbar />
          <FileSearch />
          <FileList />
        </FileBrowser>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          {intl.get('common.dialog.cancel')}
        </Button>
        <Button onClick={onSelectedFiles} disabled={selectedFiles.length === 0}>
          {intl.get('common.link.add.title')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePicker;
